// v2/components/HeaderSearch.js — ust bar arama kutusu + anlik oneri listesi
// suggest=false: sadece kutu (Urunler sayfasi; liste zaten altta canli guncellenir)
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, X, CornerDownLeft } from 'lucide-react';
import { apiGet, qs } from '../utils/api';
import useClickOutside from '../hooks/useClickOutside';
import hs from './Header.module.css';
import s from './HeaderSearch.module.css';

const GECIKME = 200;
const LIMIT = 8;
const ETIKET = { oem: 'OEM', eski: 'Eski kod', yeni: 'Yeni kod', benzer: 'Benzer' };
const BOS = { items: [], total: 0, fuzzy: false, corrected: '', q: '' };

// Sunucudaki v2_arama.kod ile uyumlu: yalnizca harf/rakam sayilir
const anlamliUzunluk = (t) => (t || '').replace(/[^0-9A-Za-zÇĞİÖŞÜçğıöşü]/g, '').length;

export default function HeaderSearch({ value = '', onChange = () => {}, suggest = true }) {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [sonuc, setSonuc] = useState(BOS);
  const [loading, setLoading] = useState(false);
  const [aktif, setAktif] = useState(-1);
  const kutu = useRef(null);
  const panel = useRef(null);
  const abortRef = useRef(null);

  const kapat = useCallback(() => { setOpen(false); setAktif(-1); }, []);
  useClickOutside(panel, kutu, kapat, open);

  const q = value.trim();
  const yeterli = suggest && anlamliUzunluk(q) >= 2;

  useEffect(() => {
    if (!yeterli) {
      if (abortRef.current) abortRef.current.abort();
      setSonuc(BOS);
      setLoading(false);
      return undefined;
    }
    const t = setTimeout(async () => {
      if (abortRef.current) abortRef.current.abort();
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      setLoading(true);
      try {
        const v = await apiGet(`/v2/catalog/suggest/${qs({ q, limit: LIMIT })}`, { signal: ctrl.signal });
        if (ctrl.signal.aborted) return;
        setSonuc({
          items: Array.isArray(v && v.results) ? v.results : [],
          total: Number(v && v.total) || 0,
          fuzzy: !!(v && v.fuzzy),
          corrected: (v && v.corrected) || '',
          q,
        });
        setAktif(-1);
      } catch (e) {
        if (e && e.name === 'AbortError') return;
        setSonuc({ ...BOS, q });
      } finally {
        if (!ctrl.signal.aborted) setLoading(false);
      }
    }, GECIKME);
    return () => clearTimeout(t);
  }, [q, yeterli]);

  useEffect(() => () => { if (abortRef.current) abortRef.current.abort(); }, []);

  const urunlereGit = useCallback((terim) => {
    kapat();
    navigate(`/v2/products?q=${encodeURIComponent(terim)}`);
  }, [kapat, navigate]);

  const guncel = sonuc.q === q;
  const goster = open && yeterli && (sonuc.q !== '' || loading);

  const onKeyDown = (e) => {
    if (!suggest) return;
    const n = guncel ? sonuc.items.length : 0;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setOpen(true);
      setAktif((a) => (n ? (a + 1) % n : -1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setAktif((a) => (n ? (a <= 0 ? n - 1 : a - 1) : -1));
    } else if (e.key === 'Enter') {
      if (!q) return;
      e.preventDefault();
      if (open && aktif >= 0 && n && sonuc.items[aktif]) urunlereGit(sonuc.items[aktif].code);
      else urunlereGit(q);
    } else if (e.key === 'Escape') {
      kapat();
    }
  };

  return (
    <div className={hs.searchWrap}>
      <div className={hs.search} ref={kutu}>
        <input
          className={hs.searchInput} type="text" value={value}
          onChange={(e) => { onChange(e.target.value); if (suggest) setOpen(true); }}
          onFocus={() => { if (suggest) setOpen(true); }}
          onKeyDown={onKeyDown}
          placeholder="Ürün kodu, OEM no veya ürün adı ara..."
          aria-label="Ürün ara"
          autoComplete="off"
          role={suggest ? 'combobox' : undefined}
          aria-expanded={suggest ? goster : undefined}
          aria-controls={suggest ? 'v2-arama-oneri' : undefined}
        />
        {value ? (
          <button type="button" className={hs.clearBtn}
                  onClick={() => { onChange(''); kapat(); }} aria-label="Aramayı temizle">
            <X size={15} />
          </button>
        ) : null}
        <span className={hs.searchIcon}>
          {loading ? <span className={s.spin} /> : <Search size={17} />}
        </span>

        {goster && (
          <div ref={panel} className={s.panel} role="listbox" id="v2-arama-oneri">
            {guncel && sonuc.fuzzy && (
              <div className={s.note}>
                {sonuc.corrected
                  ? <>“<b>{sonuc.corrected}</b>” için sonuçlar</>
                  : <>Tam eşleşme yok — benzer kodlar</>}
              </div>
            )}
            {guncel && !loading && sonuc.items.length === 0 && (
              <div className={s.empty}>“{q}” için ürün bulunamadı</div>
            )}
            <div className={guncel ? undefined : s.dim}>
              {sonuc.items.map((u, i) => (
                <button
                  key={u.code} type="button" role="option" aria-selected={i === aktif}
                  className={`${s.item} ${i === aktif ? s.itemOn : ''}`}
                  onMouseEnter={() => setAktif(i)}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => urunlereGit(u.code)}
                >
                  <span className={s.code}>{u.code}</span>
                  <span className={s.name}>{u.name}</span>
                  {ETIKET[u.match]
                    ? <span className={`${s.tag} ${s['tag_' + u.match] || ''}`}>{ETIKET[u.match]}</span>
                    : <span />}
                  <span className={`${s.stock} ${u.stock ? s.stockOn : ''}`}>
                    {u.stock ? 'Stokta' : 'Stok yok'}
                  </span>
                </button>
              ))}
            </div>
            {guncel && sonuc.total > 0 && (
              <button type="button" className={s.all}
                      onMouseDown={(e) => e.preventDefault()} onClick={() => urunlereGit(q)}>
                <span>Tüm sonuçları gör <b>({sonuc.total})</b></span>
                <CornerDownLeft size={14} />
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
